import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatUnits } from 'viem'

import { formatAbbreviatedTokenAmount } from '~/components/component-library/TokenBalance'
import { Denomination } from '~/components/component-library/TokenSelector/types'
import { TOKEN_PRICE_DECIMALS } from '~/constants/tokens'
import { useDenominationStore } from '~/store/denominationStore'
import { displayCurrency } from '~/utils/display'
import { getAmountFromUSDValue, multiplyWithPrecision } from '~/utils/numbers'

function formatUsdDisplay(usdAmount: string): string {
  const parsed = Number.parseFloat(usdAmount)
  if (!Number.isFinite(parsed)) return ''
  return (Math.round(parsed * 100) / 100).toFixed(2)
}

type UseDenominationInputOptions = {
  /** The canonical token amount from RHF (always in token units). */
  amount: string
  onAmountChange: (amount: string) => void
  denomination: Denomination
  tokenAddress: string | undefined
  tokenChainId: number | undefined
  tokenPrice: string | undefined
  tokenSymbol: string | undefined
  decimals: number
  isEurc: boolean
  isVaultPosition: boolean
  /** Selected token's on-chain balance, used to track the MAX lock. */
  selectedBalanceAmount?: bigint
}

// Invariants:
//   - `amount` is the canonical token-decimal amount (the RHF source of truth).
//   - `displayValue` is the user-visible string. In token mode
//     `displayValue === amount`; in USD mode `amount = displayValue / price`.
// Synchronization paths:
//   - User input         -> handleValueChange / setDisplayFromTokenAmount (sets both)
//   - External `amount`  -> render-time block below (resyncs displayValue)
//   - Context change     -> effect below (resyncs amount on token/price/mode flip)
//   - `lastEmittedAmountRef` dedupes so the two sync paths don't fight.
export function useDenominationInput({
  amount,
  onAmountChange,
  denomination,
  tokenAddress,
  tokenChainId,
  tokenPrice,
  tokenSymbol,
  decimals,
  isEurc,
  isVaultPosition,
  selectedBalanceAmount,
}: UseDenominationInputOptions) {
  const normalizedAmount = amount || ''

  // Read from a shared store so every toggleable input flips together
  // and the preference survives reloads.
  const internalIsUsdMode = useDenominationStore((s) => s.isUsdMode)
  const canToggle = denomination === 'switch'
  const hasPrice = !!tokenPrice && Number.parseFloat(tokenPrice) > 0
  // Force token mode when no price (can't show USD) or EURC (displayed as €).
  // Force USD mode for vault positions (share amounts are not meaningful).
  const isUsdMode =
    denomination === 'usd'
      ? true
      : denomination === 'token'
        ? false
        : isVaultPosition
          ? hasPrice
          : internalIsUsdMode && hasPrice && !isEurc
  const isUsdModeWithPrice = isUsdMode && hasPrice

  // Display value — the source of truth for what the user sees.
  // In token mode this equals the RHF amount; in USD mode it diverges.
  const [displayValue, setDisplayValue] = useState(() => normalizedAmount)
  const displayValueRef = useRef(displayValue)
  displayValueRef.current = displayValue

  // Tracks the last token amount this hook emitted via onAmountChange.
  // Used to distinguish user-initiated changes from external updates (e.g. quotes).
  const lastEmittedAmountRef = useRef(normalizedAmount)

  // When MAX is clicked, the canonical token amount is pinned to the exact
  // balance and re-emitted as balance/price/decimals change. This prevents
  // USD-display rounding from drifting the canonical amount above balance.
  const [maxLocked, setMaxLocked] = useState(false)

  // Releasing the lock when the selected token changes prevents the MAX
  // intent from silently transferring to a different token's balance.
  const prevTokenAddressRef = useRef(tokenAddress)
  const prevTokenChainIdRef = useRef(tokenChainId)
  if (
    tokenAddress !== prevTokenAddressRef.current ||
    tokenChainId !== prevTokenChainIdRef.current
  ) {
    prevTokenAddressRef.current = tokenAddress
    prevTokenChainIdRef.current = tokenChainId
    if (maxLocked) setMaxLocked(false)
  }

  const emitAmount = useCallback(
    (amount: string) => {
      lastEmittedAmountRef.current = amount
      onAmountChange(amount)
    },
    [onAmountChange],
  )

  // --- Converters ---

  const convertUsdToTokenAmount = useCallback(
    (usdAmount: string) => {
      if (!tokenPrice) return undefined
      return getAmountFromUSDValue({
        tokenUSDValue: usdAmount,
        tokenDecimals: decimals,
        tokenPrice,
      })
    },
    [decimals, tokenPrice],
  )

  const convertTokenToUsdAmount = useCallback(
    (tokenAmount: string) => {
      if (!tokenPrice) return undefined
      return multiplyWithPrecision(
        { amount: tokenAmount, decimal: decimals },
        { amount: tokenPrice, decimal: TOKEN_PRICE_DECIMALS },
      )
    },
    [decimals, tokenPrice],
  )

  const convertTokenToUsdDisplay = useCallback(
    (tokenAmount: string) => {
      const usdAmount = convertTokenToUsdAmount(tokenAmount)
      if (usdAmount === undefined) return undefined
      return formatUsdDisplay(usdAmount)
    },
    [convertTokenToUsdAmount],
  )

  // External sync (during render): when normalizedAmount changes from an
  // external source (e.g. quote result), update display before paint.
  const prevNormalizedAmountRef = useRef(normalizedAmount)
  if (normalizedAmount !== prevNormalizedAmountRef.current) {
    prevNormalizedAmountRef.current = normalizedAmount
    if (normalizedAmount !== lastEmittedAmountRef.current) {
      lastEmittedAmountRef.current = normalizedAmount
      if (!normalizedAmount) {
        if (displayValueRef.current) setDisplayValue('')
      } else if (isUsdMode) {
        const usdDisplay = convertTokenToUsdDisplay(normalizedAmount)
        if (usdDisplay !== undefined) setDisplayValue(usdDisplay)
      } else {
        setDisplayValue(normalizedAmount)
      }
    }
  }

  // Convert the display value when the user toggles the shared preference.
  // Tracks the raw store value, not the resolved `isUsdMode`, so that
  // token-forced mode flips (EURC, no-price) fall through to the
  // post-effect's reinterpret behaviour instead of converting.
  const prevInternalIsUsdModeRef = useRef(internalIsUsdMode)
  if (internalIsUsdMode !== prevInternalIsUsdModeRef.current) {
    prevInternalIsUsdModeRef.current = internalIsUsdMode
    const currentDisplay = displayValueRef.current
    if (
      currentDisplay &&
      hasPrice &&
      canToggle &&
      !isEurc &&
      !isVaultPosition
    ) {
      if (internalIsUsdMode) {
        const sourceTokenAmount = maxLocked ? normalizedAmount : currentDisplay
        const usdDisplay = convertTokenToUsdDisplay(sourceTokenAmount)
        if (usdDisplay !== undefined) setDisplayValue(usdDisplay)
      } else {
        const tokenAmount = maxLocked
          ? normalizedAmount
          : convertUsdToTokenAmount(currentDisplay)
        if (tokenAmount) setDisplayValue(tokenAmount)
      }
    }
  }

  // On context change (token swap, price refresh, USD/token mode flip),
  // realign `amount` with `displayValue`:
  //   - maxLocked: pin amount to the exact balance and re-derive display
  //     from it. USD-display rounding must not drift the canonical amount
  //     above balance.
  //   - USD mode: amount = displayValue / price.
  //   - Token mode: amount = displayValue. Without this branch, switching
  //     to a token that forces a mode flip (e.g. EURC) would leave a stale
  //     amount from the previous token.
  // Display is read from a ref so this does NOT fire on keystrokes.
  useEffect(() => {
    if (maxLocked) {
      if (selectedBalanceAmount === undefined) return
      const tokenAmount = formatUnits(selectedBalanceAmount, decimals)
      if (isUsdMode) {
        const usdDisplay = convertTokenToUsdDisplay(tokenAmount)
        if (usdDisplay !== undefined) setDisplayValue(usdDisplay)
      } else {
        setDisplayValue(tokenAmount)
      }
      emitAmount(tokenAmount)
      return
    }
    const currentDisplay = displayValueRef.current
    if (!currentDisplay) return
    if (isUsdModeWithPrice) {
      const tokenAmount = convertUsdToTokenAmount(currentDisplay)
      if (tokenAmount !== undefined) emitAmount(tokenAmount)
    } else {
      emitAmount(currentDisplay)
    }
  }, [
    isUsdMode,
    isUsdModeWithPrice,
    maxLocked,
    selectedBalanceAmount,
    decimals,
    convertUsdToTokenAmount,
    convertTokenToUsdDisplay,
    emitAmount,
  ])

  // --- Handlers ---

  const handleValueChange = useCallback(
    (value: string) => {
      if (maxLocked) setMaxLocked(false)
      setDisplayValue(value)
      if (!value) {
        emitAmount('')
        return
      }
      if (!isUsdModeWithPrice) {
        emitAmount(value)
        return
      }
      const tokenAmount = convertUsdToTokenAmount(value)
      emitAmount(tokenAmount ?? '')
    },
    [isUsdModeWithPrice, emitAmount, convertUsdToTokenAmount, maxLocked],
  )

  // Mutates only displayValue; the context-change effect above re-emits
  // `amount` after the mode flips.
  const handleToggleMode = useCallback(() => {
    if (!hasPrice || !canToggle) return
    useDenominationStore.getState().toggle()
  }, [hasPrice, canToggle])

  /**
   * Set the display from a known token amount (used by preset buttons).
   * Pass `lock: true` to pin the canonical amount to balance across price
   * ticks — see the maxLocked tracking effect.
   */
  const setDisplayFromTokenAmount = useCallback(
    (tokenAmount: string, options?: { lock?: boolean }) => {
      if (isUsdModeWithPrice) {
        const usdDisplay = convertTokenToUsdDisplay(tokenAmount)
        if (usdDisplay !== undefined) setDisplayValue(usdDisplay)
      } else {
        setDisplayValue(tokenAmount)
      }
      emitAmount(tokenAmount)
      setMaxLocked(options?.lock ?? false)
    },
    [isUsdModeWithPrice, convertTokenToUsdDisplay, emitAmount],
  )

  // --- Derived display ---

  const convertedDisplay = useMemo(() => {
    if (!displayValue || !hasPrice || !tokenAddress || isVaultPosition)
      return undefined

    if (isUsdMode) {
      const tokenAmount = convertUsdToTokenAmount(displayValue)
      if (tokenAmount === undefined) return undefined
      const formatted = formatAbbreviatedTokenAmount(parseFloat(tokenAmount), {
        address: tokenAddress,
        chainId: tokenChainId ?? 1,
      })
      return `${formatted} ${tokenSymbol ?? ''}`
    }

    const usdAmount = convertTokenToUsdAmount(displayValue)
    if (usdAmount === undefined) return undefined
    return displayCurrency(parseFloat(usdAmount), {
      showLessThanForSmallValues: true,
    })
  }, [
    displayValue,
    isUsdMode,
    isVaultPosition,
    hasPrice,
    tokenAddress,
    tokenChainId,
    tokenSymbol,
    convertUsdToTokenAmount,
    convertTokenToUsdAmount,
  ])

  // EURC formatting: use euro when EURC is selected and not in USD mode
  const useEuroFormatting = isEurc && !isUsdMode
  const showToggle = canToggle && !isEurc && !isVaultPosition

  return {
    displayValue,
    isUsdMode,
    showToggle,
    handleValueChange,
    handleToggleMode,
    setDisplayFromTokenAmount,
    convertedDisplay,
    // Formatting props for NumericFormat
    prefix: useEuroFormatting ? '€' : isUsdMode ? '$' : undefined,
    placeholder: useEuroFormatting ? '€0.00' : isUsdMode ? '$0.00' : '0.00',
    decimalScale: isUsdMode ? 2 : decimals,
    thousandSeparator: useEuroFormatting ? '.' : ',',
    decimalSeparator: useEuroFormatting ? ',' : '.',
  }
}
